import { useCallback, useEffect, useState } from 'react';
import { notificationsGateway } from './index';
import type { NotificationsGateway, StoredNotification } from './index';

const REFRESH_MS = 60_000;
const REFRESH_EVENT = 'epn:notifications-refresh';

/** Asks the shell to re-read the stored notifications now (call after an action that can publish one, such as approving a batch). */
export function requestNotificationsRefresh(): void {
  window.dispatchEvent(new Event(REFRESH_EVENT));
}

export interface StoredNotificationsState {
  items: StoredNotification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

/**
 * The signed-in user's stored notifications (Identity). Failures never break the shell: a user who cannot read them, an expired session or an
 * unreachable service simply gets an empty list. Refreshed when the page changes and once a minute; reads are marked optimistically and
 * re-synced from the server.
 */
export function useStoredNotifications(refreshKey: string, gateway: NotificationsGateway = notificationsGateway): StoredNotificationsState {
  const [items, setItems] = useState<StoredNotification[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const id = window.setInterval(bump, REFRESH_MS);
    window.addEventListener(REFRESH_EVENT, bump);
    window.addEventListener('focus', bump);
    return () => {
      window.clearInterval(id);
      window.removeEventListener(REFRESH_EVENT, bump);
      window.removeEventListener('focus', bump);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    gateway
      .list(controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setItems(r.items);
      })
      .catch(() => {
        if (!controller.signal.aborted) setItems([]);
      });
    return () => controller.abort();
  }, [gateway, refreshKey, tick]);

  const resync = useCallback(() => setTick((t) => t + 1), []);

  const markRead = useCallback(
    (id: string) => {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      gateway.markRead(id).then(resync, resync);
    },
    [gateway, resync],
  );

  const markAllRead = useCallback(() => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    gateway.markAllRead().then(resync, resync);
  }, [gateway, resync]);

  return { items, unreadCount: items.filter((n) => !n.isRead).length, markRead, markAllRead };
}
